var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useAlertUiTranslation } from '../../../../../ui/hooks';
import { buildGeneralTab, buildValidateTab, showWidgetEditDialog } from '@juvare/common-form-ui/WidgetEditDialog';
import { FormHelperErrorDescription } from '@juvare/common-form-ui/WidgetForm/Components';
import { defaultWidgetLabelProvider, getVisibleError, handleWidgetUpdate, renderForm, validateCustom, } from '@juvare/common-form-ui/WidgetForm/Utils';
import { handleErrorSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { useOrganizationStorage } from '@juvare/contact-management/AppClients';
import { orgIdPrefixForAlertService } from '@juvare/contact-management/backend/features/organizations/shared';
import { requireAnyOrgPermission, requireAnySystemPermission } from '@juvare/contact-management/backend/features/permissions/shared';
import { showOrganizationTreeDialog } from '@juvare/contact-management/ui/features/organizations';
import { useUserState } from '@juvare/contact-management/ui/hooks';
import { ContactManagementConfigContext } from '@juvare/contact-management/ui/providers';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import { CircularProgress, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';
import { type } from './types';
const WidgetSender = (props) => {
    var _a, _b, _c;
    const { t } = useAlertUiTranslation();
    const { appTitle } = useSelector(getSettings);
    const { appName } = React.useContext(ContactManagementConfigContext);
    const orgStorage = useOrganizationStorage();
    const userState = useUserState();
    const [isLoading, setIsLoading] = React.useState(true);
    const externalId = props.getData();
    let orgId = '';
    if (externalId && externalId.startsWith('org_')) {
        orgId = externalId.replace('org_', '');
    }
    const [options, setOptions] = React.useState(() => {
        const options = [];
        // allow see myself as sender
        if (requireAnySystemPermission(userState, 'manage-notifications') ||
            requireAnyOrgPermission(userState, 'view-and-message-members')) {
            options.push({
                id: `account_${userState.id}`,
                name: t('widget.sender.myself'),
            });
        }
        // allow see system as sender
        if (requireAnySystemPermission(userState, 'manage-notifications') ||
            (requireAnyOrgPermission(userState, 'view-and-message-members', 'create-notifications') &&
                requireAnyOrgPermission(userState, 'notify-as-system'))) {
            options.push({
                id: `system_${appName}`,
                name: appTitle || 'System',
            });
        }
        // allow see organization as sender
        if (requireAnySystemPermission(userState, 'manage-notifications') ||
            requireAnyOrgPermission(userState, 'view-and-message-members', 'create-notifications')) {
            options.push({
                id: 'selectOrganization',
                name: t('widget.sender.selectOrg'),
            });
        }
        return options;
    });
    const error = getVisibleError(props);
    React.useEffect(() => {
        if (orgId) {
            function fetchOrgData() {
                return __awaiter(this, void 0, void 0, function* () {
                    try {
                        const response = yield orgStorage.list({ filter: [{ id: orgId, op: '=' }] });
                        if (response.rows.length) {
                            const org = response.rows[0];
                            setOptions((prev) => {
                                if (prev.some((opt) => opt.id === orgIdPrefixForAlertService + org.id)) {
                                    return prev;
                                }
                                return [...prev, { id: orgIdPrefixForAlertService + org.id, name: org.columns.name }];
                            });
                        }
                        else {
                            handleErrorSnackbar(new Error(t('pages.edit.errors.senderNotFound')));
                        }
                    }
                    catch (error) {
                        handleErrorSnackbar(error);
                    }
                    finally {
                        setIsLoading(false);
                    }
                });
            }
            fetchOrgData();
        }
        else {
            setIsLoading(false);
        }
    }, [externalId, orgId, orgStorage, t]);
    const handleChange = (value) => {
        if (value === 'selectOrganization') {
            showOrganizationTreeDialog({
                staticFilters: {
                    orgPermissions: requireAnySystemPermission(userState, 'all-organizations-admin')
                        ? undefined
                        : ['view-and-message-members', 'create-notifications'],
                },
                multiSelect: false,
                onChange: (selectedOrganizationIds) => {
                    if (selectedOrganizationIds.length > 0) {
                        props.setData(`org_${selectedOrganizationIds[0]}`);
                        renderForm(props, { changed: true });
                    }
                },
            });
        }
        else {
            props.setData(value);
            renderForm(props, { changed: true });
        }
    };
    if (isLoading) {
        return React.createElement(CircularProgress, null);
    }
    return (React.createElement(FormControl, { variant: 'outlined', fullWidth: true, required: (_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required, error: !!error },
        React.createElement(InputLabel, null, props.config.label),
        React.createElement(Select, { label: props.config.label, onChange: (event) => handleChange(event.target.value), "data-test": 'sender-select', value: ((_b = options.find((opt) => opt.id === externalId)) === null || _b === void 0 ? void 0 : _b.id) || '', disabled: (_c = props.config) === null || _c === void 0 ? void 0 : _c.disabled, sx: { backgroundColor: 'background.paper' }, inputProps: { 'aria-label': props.config.label } }, options.map((option) => (React.createElement(MenuItem, { key: option.id, value: option.id }, option.name)))),
        React.createElement(FormHelperErrorDescription, { error: error })));
};
const WidgetSenderEditor = (props) => {
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, {
            title: props.config.label,
        }, buildGeneralTab(props, { key: true, label: true }), buildValidateTab(props, { required: true })) }),
        React.createElement(WidgetSender, Object.assign({}, props))));
};
export const widgetSender = {
    type,
    createWidget: () => ({
        type,
        label: 'Sender',
        key: 'ownerId',
    }),
    validate: (props) => {
        var _a, _b;
        const value = props.getDataStringOrUndefined();
        if (((_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required) && !value) {
            return (_b = props.config.validate.requiredMessage) !== null && _b !== void 0 ? _b : (props.config.label ? props.config.label + ' is required' : 'Required');
        }
        return validateCustom(props, value);
    },
    update: handleWidgetUpdate,
    component: WidgetSender,
    icon: React.createElement(ContactMailIcon, null),
    editorComponent: WidgetSenderEditor,
};
